/* globals window */

import React, { useState, useEffect } from 'react';
import {
  QueryClient,
  QueryClientProvider,
  useInfiniteQuery,
} from 'react-query';
import {
  stroot,
  organisationMissions as organisationMissionsAPI,
  mission as missionAPI,
} from '@birdi/js-sdk';
import { css } from '@emotion/react';
import { useNavigate, Link, useLocation, useParams } from 'react-router';
import { FeatureFlagNames } from '@birdi/js-sdk/src/public/organisation-features';
import { useMetaSelector } from '../meta/reducer';
import { INTERCOM_TOUR_CODES } from '../common/consts';
import PlayCircleSVG from '@birdi/icons/play-circle.svg';
import BookOpenSVG from '@birdi/icons/book-open.svg';
import VideoMidnightSVG from '@birdi/icons/video-midnight.svg';
import { SiteSelect } from '@birdi/select/SiteSelect';
import { buttonOutlineBlue, breakpoints, blocks, forms } from '@birdi/theme';
import { getInitialSearchParams } from '@birdi/utils/src/search-params';
import { NewMapList } from '../missions/new-map-list';
import { NewMapTable } from '../missions/new-map-table';
import { DateField } from './date-picker';
import { LocationSelect } from '@birdi/google-places-autocomplete';
import type { Location } from '@birdi/types';
import { maps as mapsAPI } from '@birdi/js-sdk';
import type { Map } from '@birdi/js-sdk/src/public/maps';
import { MapDashboardHeader } from '../common/map-dashboard-header';
import { Button } from '@birdi/components-common';
import { useMapCovers } from '../dashboard-new/use-map-covers';
import { useMapFiles } from '../dashboard-new/use-map-files';
import CardViewSVG from '@birdi/icons/card-view.svg';
import TableViewSVG from '@birdi/icons/list-view.svg';
import ChevronSVG from '@birdi/icons/chevron-down.svg';
import FolderSelect from './folder-select';
import { UserScopeSelect } from '@birdi/select/UserScopeSelect';

const queryClient = new QueryClient();

const LOAD_AMOUNT = 8;

type MapsProps = {
  workspaceId: string;
  view: string;
  initialFolderId?: string | null;
};

interface SearchState {
  mapName: string | null;
  startDate: Date | null;
  endDate: Date | null;
  location: any;
  folderId: string | null;
  creatorScopeId: string | null;
}

function Maps({ workspaceId, view, initialFolderId }: MapsProps) {
  const navigate = useNavigate();
  const meta = useMetaSelector();
  const location = useLocation();

  // Get initial location from navigation state
  const initialLocation = (location.state as any)?.location || null;

  const [state, setState] = useState<SearchState>({
    mapName: null,
    startDate: null,
    endDate: null,
    location: initialLocation,
    folderId: initialFolderId || null,
    creatorScopeId: null,
  });

  const [showMoreOptions, setShowMoreOptions] = useState(false);

  // Update state when initialFolderId changes (e.g., when URL parameter changes)
  useEffect(() => {
    if (initialFolderId !== state.folderId) {
      setState((prevState) => ({
        ...prevState,
        folderId: initialFolderId || null,
      }));
      fetchFunction('folderId', initialFolderId || null);
    }
  }, [initialFolderId]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery(
      [
        'missionsData',
        state.mapName,
        state.startDate,
        state.endDate,
        state.location,
        state.folderId,
        state.creatorScopeId,
      ],
      async ({ pageParam = 1 }) => {
        const apiCall = mapsAPI.getOrganisationMaps;
        return apiCall(stroot('qgtmps'), workspaceId, {
          start: pageParam,
          limit: LOAD_AMOUNT,
          noCover: 'yes',
          ...(state.mapName && { mapName: state.mapName }),
          ...(state.startDate && { startDate: state.startDate.toISOString() }),
          ...(state.endDate && { endDate: state.endDate.toISOString() }),
          ...(state.location && {
            locationBbox: JSON.stringify(state.location?.bbox),
          }),
          ...(state.location?.suburbName && {
            suburbName: JSON.stringify(state.location?.suburbName),
          }),
          ...(state.folderId && {
            folderId: state.folderId,
          }),
          ...(state.creatorScopeId && {
            creatorScopeId: state.creatorScopeId,
          }),
        }).then((res) => res.body);
      },
      {
        getNextPageParam: (lastPage, allPages) => {
          return lastPage.length === LOAD_AMOUNT
            ? allPages.length + 1
            : undefined;
        },
        keepPreviousData: true,
      },
    );

  // Type guard to ensure item is a valid Map
  const isMap = (item: unknown): item is Map => {
    return (
      typeof item === 'object' &&
      item !== null &&
      'id' in item &&
      'name' in item &&
      'createdAt' in item
    );
  };

  // Fetch covers and files asynchronously when maps change
  const allMaps: Map[] = data?.pages
    ? data.pages.flat().filter(isMap)
    : [];
  const { getMapsWithCovers } = useMapCovers(allMaps);
  const { getMapsWithFiles } = useMapFiles(allMaps);

  // Merge both covers and files into maps
  const getMapsWithAllData = (mapsToMerge: Map[]) => {
    const mapsWithCovers = getMapsWithCovers(mapsToMerge);
    return getMapsWithFiles(mapsWithCovers);
  };

  const anySearchParams = !!(
    state.mapName ||
    state.location ||
    state.startDate ||
    state.endDate ||
    state.folderId ||
    state.creatorScopeId
  );
  // const anySearchParams = false;

  const handleLoadMore = () => {
    fetchNextPage();
  };

  const fetchFunction = (
    item: string | null = null,
    value: string | Date | any | null = null,
  ) => {
    const updatedArray = [
      'missionsData',
      item === 'mapName' ? value : state.mapName,
      item === 'startDate' ? value : state.startDate,
      item === 'endDate' ? value : state.endDate,
      item === 'location' ? value : state.location,
      item === 'folderId' ? value : state.folderId,
      item === 'creatorScopeId' ? value : state.creatorScopeId,
    ];
    queryClient.invalidateQueries(updatedArray);
  };

  const updateMapNameFilter = (mapName: string) => {
    setState((prevState) => ({ ...prevState, mapName }));
    fetchFunction('mapName', mapName);
  };

  const handleDateChange = (label: string, selectedDate: Date) => {
    if (label === 'startDate') {
      setState((prevState) => ({ ...prevState, startDate: selectedDate }));
      fetchFunction('startDate', selectedDate);
    } else if (label === 'endDate') {
      setState((prevState) => ({ ...prevState, endDate: selectedDate }));
      fetchFunction('endDate', selectedDate);
    }
  };

  const handleLocationChange = (selectedLocation: any | null) => {
    navigate(location.pathname, { replace: true, state: {} });
    setState((prevState) => ({ ...prevState, location: selectedLocation }));
    fetchFunction('location', selectedLocation);
  };

  const handleCreatorChange = (selectedCreatorScopeId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      creatorScopeId: selectedCreatorScopeId,
    }));
    fetchFunction('creatorScopeId', selectedCreatorScopeId);
  };

  return (
    <div>
      <div
        css={(theme) => css`
          border-bottom: solid 1px ${theme.mono30};
        `}
      >
        <form
          role="search"
          css={css`
            padding: 1rem;
            // max-width: 72rem;
          `}
          onSubmit={(event) => event.preventDefault()}
        >
          <div
            css={css`
              display: flex;
              flex-direction: row;
              gap: 1em;
              width: 100%;
              @media (max-width: ${breakpoints.xs}) {
                flex-direction: column;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                flex: 1;
              `}
            >
              <input
                type="text"
                value={state.mapName || undefined}
                placeholder="Filter by map name"
                css={(theme) => css`
                  ${blocks.input(theme)};
                  width: 100%;
                `}
                onChange={(event) => {
                  event.preventDefault();
                  updateMapNameFilter(event.target.value);
                }}
              />
            </div>
            <div
              css={css`
                display: flex;
                flex: 1;
              `}
            >
              <LocationSelect
                inputId="location"
                styles={{
                  ...forms.reactSelect,
                  container: (base) => ({
                    ...base,
                    width: '100%',
                  }),
                }}
                defaultValue={state.location?.name || ''}
                value={state.location}
                placeholder="Filter by map location"
                isClearable
                onLocationSelect={(geocode) => {
                  handleLocationChange(geocode.location);
                }}
                onClear={() => handleLocationChange(null)}
              />
            </div>
            <div
              css={css`
                display: flex;
                flex: 1;
                align-items: center;
                gap: 0.5rem;
              `}
            >
              <span>Date:</span>
              <span>From</span>
              <DateField
                label="startDate"
                selectedDate={state.startDate}
                onChange={handleDateChange}
                endDate={state.endDate}
              />
              <span>To</span>
              <DateField
                label="endDate"
                selectedDate={state.endDate}
                onChange={handleDateChange}
                startDate={state.startDate}
              />
            </div>
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: flex-end;
                min-width: 120px;
              `}
            >
              <button
                type="button"
                onClick={() => setShowMoreOptions(!showMoreOptions)}
                css={(theme) => css`
                  background: none;
                  border: none;
                  color: ${theme.deepOcean};
                  text-decoration: underline;
                  cursor: pointer;
                  font-size: 0.875rem;
                  padding: 5px;
                  display: flex;
                  align-items: center;
                  gap: 0.25rem;

                  &:hover {
                    background-color: ${theme.lightOcean};
                  }
                `}
              >
                More options
                <span
                  css={css`
                    transform: ${showMoreOptions
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)'};
                    transition: transform 0.2s ease;
                  `}
                >
                  <ChevronSVG />
                </span>
              </button>
            </div>
          </div>
          {showMoreOptions && (
            <div
              css={css`
                display: flex;
                flex-direction: row;
                gap: 1rem;
                margin-top: 1rem;
                @media (max-width: ${breakpoints.xs}) {
                  flex-direction: column;
                }
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex: 1;
                `}
              >
                <FolderSelect
                  onChange={(option) => {
                    const folderId = option?.value || null;
                    setState((prevState) => ({ ...prevState, folderId }));
                    fetchFunction('folderId', folderId);
                  }}
                  defaultValue={state.folderId || null}
                  placeholder="All folders"
                  isClearable={true}
                  isDisabled={!!initialFolderId}
                />
              </div>
              <div
                css={css`
                  display: flex;
                  flex: 1;
                `}
              >
                <UserScopeSelect
                  workspaceId={
                    meta.workspace?.id || meta.organisation.id || null
                  }
                  userScopeId={state.creatorScopeId}
                  isClearable
                  onChange={(option, action) => {
                    if (action.action === 'select-option') {
                      handleCreatorChange(option.data.scopeId);
                    } else if (action.action === 'clear') {
                      handleCreatorChange(null);
                    }
                  }}
                  styles={{
                    ...forms.reactSelect,
                    container: (base) => ({
                      ...base,
                      width: '100%',
                    }),
                  }}
                  placeholder="Filter by map creator"
                />
              </div>
              <div
                css={css`
                  display: flex;
                  flex: 1;
                `}
              ></div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: flex-end;
                  min-width: 120px;
                `}
              ></div>
            </div>
          )}
        </form>
      </div>
      <div
        css={css`
          padding: 0 1rem;
          box-sizing: border-box;
          margin-top: 1em;
        `}
      >
        <div>
          {isLoading && (
            <div style={{ padding: '2em', textAlign: 'center' }}>
              Loading ...
            </div>
          )}
          {data && data.pages[0].length !== 0 && view === 'table-view' && (
            <NewMapList maps={getMapsWithAllData(allMaps)} />
          )}
          {data && data.pages[0].length !== 0 && view === 'card-view' && (
            <NewMapTable maps={getMapsWithAllData(allMaps)} />
          )}
          {hasNextPage ? (
            isFetchingNextPage ? (
              <span>Loading more...</span>
            ) : (
              <button
                css={(theme) => css`
                  ${buttonOutlineBlue(theme)};
                  width: 100%;
                `}
                onClick={handleLoadMore}
              >
                Load More ...
              </button>
            )
          ) : null}
          {data && data.pages[0].length === 0 && anySearchParams && (
            <p
              css={css`
                width: calc(100% - 1rem);
              `}
            >
              No maps found
            </p>
          )}
          {data && data.pages[0].length === 0 && !anySearchParams && (
            <p
              css={(theme) => css`
                width: calc(100% - 1rem);
                color: ${theme.mono50};
              `}
            >
              You have no Birdi 2.0 maps.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const AllNewMaps: React.FC = () => {
  const meta = useMetaSelector();
  const workspaceId = meta.workspace?.id || meta.organisation.id;
  const { folderId } = useParams<{ folderId?: string }>();

  const [view, setView] = useState(() => {
    const savedView = localStorage.getItem('all-maps-view');
    return savedView || 'table-view';
  });

  // Clear saved view when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      localStorage.removeItem('all-maps-view');
    };
  }, []);

  return (
    <div>
      <MapDashboardHeader
        titleComponent={
          <h2
            css={css`
              margin: 0;
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            Your Workspace's Birdi 2.0 maps
          </h2>
        }
        folderId={folderId}
      />
      <div css={blocks.pageDescription}>
        <div
          css={css`
            display: flex;
            flex-direction: row;
            gap: 1rem;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <p>These are all your maps, created by you or shared with you.</p>
          <div>
            <button
              onClick={() => {
                const newView =
                  view === 'card-view' ? 'table-view' : 'card-view';
                setView(newView);
                localStorage.setItem('all-maps-view', newView);
              }}
              css={(theme) => css`
                svg {
                  color: ${theme.midnight};
                }
              `}
            >
              {view === 'card-view' && (
                <CardViewSVG
                  css={css`
                    font-size: 1.25em;
                  `}
                />
              )}
              {view === 'table-view' && (
                <TableViewSVG
                  css={css`
                    font-size: 1.75em;
                  `}
                />
              )}
            </button>
          </div>
        </div>
      </div>
      <QueryClientProvider client={queryClient}>
        {workspaceId && (
          <Maps
            workspaceId={workspaceId}
            view={view}
            initialFolderId={folderId}
          />
        )}
      </QueryClientProvider>
    </div>
  );
};

export { AllNewMaps };
